import { useContext, useEffect, useRef, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import './index.scss';
import 'react-toastify/dist/ReactToastify.css';
import Layout from './components/layout/Layout';
import LandingPage from './components/layout/LandingPage.jsx';
import ProcessSelector from './components/layout/ProcessSelector';
import ProcessStart from './components/processes/ProcessStart';
import Discarding from './components/processes/discarding/Discarding';
import Harvesting from './components/processes/harvesting/Harvesting';
import Reporting from './components/processes/reporting/Reporting';
import Weighing from './components/processes/weighing/Weighing';
import Repositioning from './components/processes/repositioning/Repositioning';
import Seeding from './components/processes/seeding/Seeding';
import Transplanting from './components/processes/transplanting/Transplanting';
import Photographing from './components/processes/photographing/Photographing';
import TemplateManagement from './components/template-management/TemplateManagement';
import AuthContext from './contexts/auth/AuthContext';
import ProcessContext from './contexts/process/ProcessContext';
import {ALLOWED_DEEP_LINK_PATHS, MANAGEMENT_ROUTES, PROCESS_ROUTES, PROCESS_TEARDOWN_ROUTES, PROTECTED_PATH_PREFIXES, startsWithAny,} from './constants/processRouteConfig';
import {Hub} from 'aws-amplify/utils';
import {signInWithRedirect, signOut as amplifySignOut} from 'aws-amplify/auth';
import SeedTypeManagementState from './contexts/seed-type-management/SeedTypeManagementState';
import TenantManagement from './components/TenantManagement/TenantManagement';
import GlobalUserManager from './components/GlobalUserManagement/GlobalUserManager';
import List from './components/management/tenantUsers/List';
import ChangePassword from './components/Authentication/ChangePassword';
import LoginScreen from './components/ui-elements/LoginScreen';
import Loading from './components/ui-elements/Loading';
import { ALLOWEDROLES, MARKETING, OPERATOR, TENANT_ADMIN } from './constants/roles/roles';
import ProduceTypeRouting from './constants/routes/ProduceTypeRouting';
import SeedTypeRouting from './constants/routes/SeedTypeRouting';
import ContainerRouting from './constants/routes/ContainerRouting';
import ProduceTypeProvider from './contexts/management/produceTypes/ProduceTypeProvider';
import HarvestingProceduresProvider from './contexts/management/harvestingProcedures/HarvestingProceduresProvider';
import SessionInformationContext from './contexts/session-information/SessionInformationContext';
import {SignalRProvider} from './contexts/signalR/SignalRProvider';
import PlantTaskList from './components/plant-tasks/PlantTaskList.jsx';
import MajorUpdateModal from './components/MajorUpdateModal';
import MaintenanceTask from '@/components/maintenance-task/MaintenanceTask.jsx';

const App = () => {
    const { activeProcessId, finishProcess } = useContext(ProcessContext);
    const authRedirectKey = "auth:postLoginRedirect";
    const {auth, loading, isAuthenticated, accessibleTenants, currentRoles, isGlobalAdmin, currentTenant} = useContext(AuthContext);
    const [authDone, setAuthDone] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const isMobile2BEnabled = ['alpha', 'development', 'development-remote'].includes(import.meta.env.VITE_ENV);
    const landingPageSlides = [
        <ProcessSelector key="process-selector" />,
        <PlantTaskList key="plant-task-list" />,
        ...(isMobile2BEnabled ? [<MaintenanceTask key="maintenance-task" />] : []),
    ];

    const { sessionInformation } = useContext(SessionInformationContext);

    const previousPathRef = useRef(location.pathname);

    const checkAuthState = async () => {
        if (sessionInformation && !authDone) {

            auth().catch((err) => {
                console.error('Error while authenticating: ', err);
                amplifySignOut().catch((err) => {
                    console.error('Error while signing out. ', err);
                });
            }).then(() => {
                setAuthDone(true);
            });
        };
    };

    // If an unauthenticated user lands on protected/deep-link routes,
    // trigger hosted login and keep the original path for post-login redirect.
    useEffect(() => {
        if (!isAuthenticated) {
            return;
        }

        const potentialRedirectUrl = sessionStorage.getItem(authRedirectKey);
        if (!potentialRedirectUrl) {
            return;
        }

        sessionStorage.removeItem(authRedirectKey);

        const currentUrl = location.pathname + location.search + location.hash;
        if (potentialRedirectUrl !== currentUrl) {
            navigate(potentialRedirectUrl, { replace: true });
        }
    }, [isAuthenticated, navigate, location.pathname, location.search, location.hash]);

    // Central abort hook:
    // whenever we leave any process route (for example via management menu),
    // ask ProcessContext to finish/delete the active process.
    useEffect(() => {
        if (!isAuthenticated) {
            const { pathname, search } = location;
            const pathNormalized = pathname.toLowerCase();
            const parameters = new URLSearchParams(search);
            const isDeepLink = Object.values(ALLOWED_DEEP_LINK_PATHS).includes(pathNormalized) && parameters.has('taskId');
            const isPathProtected = Object.values(PROTECTED_PATH_PREFIXES).some(prefix =>
                pathNormalized.startsWith(prefix)
            );
            // if the user is not authenticated and there is a protected route, redirect to login, and redirect after login
            if (isPathProtected || isDeepLink) {
                const fullPath = pathname + search;
                signInWithRedirect({
                    customState: fullPath
                });

            }
            return;
        }
    }, [isAuthenticated]);

    // Safety redirect:
    // process screens should not stay open without an active process id.
    useEffect(() => {
        // Listen for authentication events
        const unsubscribeAuthListener = Hub.listen('auth', ({ payload }) => {
            // On sign-in, check auth state
            if (payload.event === 'signIn') {
                checkAuthState();
            }
            // check if the user had a deep link to a process before authentication
            if (payload.event === 'customOAuthState') {
                var redirectPath = payload.data;
                navigate(redirectPath, { replace: true });
            }
        });
        // unsubscribe from the listener when the component unmounts
        return () => unsubscribeAuthListener();
    }, [navigate]);

    useEffect(() => {
        const previousPath = previousPathRef.current;
        const currentPath = location.pathname.toLowerCase();
        const leftActiveProcessRoute =
            startsWithAny(previousPath.toLowerCase(), PROCESS_TEARDOWN_ROUTES) &&
            !startsWithAny(currentPath, PROCESS_TEARDOWN_ROUTES);

        if (leftActiveProcessRoute && activeProcessId) {
            finishProcess();
        }

        previousPathRef.current = location.pathname;
    }, [location.pathname, activeProcessId, finishProcess]);

    useEffect(() => {
        checkAuthState();
        if (startsWithAny(location.pathname.toLowerCase(), Object.values(PROCESS_ROUTES)) && activeProcessId == null) {
            navigate('/');
        }
    }, [sessionInformation, location.pathname, activeProcessId, navigate]);

    const renderRoutes = () => {
        const isTenantAdmin = currentRoles?.includes(TENANT_ADMIN);
        const isOperator = currentRoles?.includes(OPERATOR);
        const isMarketing = currentRoles?.includes(MARKETING);

        if (isAuthenticated && authDone && !isGlobalAdmin && (!ALLOWEDROLES.some(r => currentRoles?.includes(r)) || accessibleTenants.length === 0)) {
            return (
                <div className='image'>
                    <h2>Welcome to FarmerApp</h2>
                    <p>To access any processes please ask your administrator to assign you to a tenant.</p>
                </div>
            );
        }

        if (isAuthenticated && authDone && accessibleTenants.length > 0 && currentTenant != null) {
            return (
                <SignalRProvider >
                    <MajorUpdateModal />
                    <Routes>
                        {/* Authenticated Routes */}
                        <Route path={`${PROCESS_ROUTES.SEEDING}/*`} element={<Seeding />} />
                        <Route path={`${PROCESS_ROUTES.TRANSPLANTING}/*`} element={<Transplanting />} />
                        <Route path={`${PROCESS_ROUTES.DISCARDING}/*`} element={<Discarding />} />
                        <Route path={`${PROCESS_ROUTES.REPOSITIONING}/*`} element={<Repositioning path={PROCESS_ROUTES.REPOSITIONING} />} />
                        <Route path={`${PROCESS_ROUTES.HARVESTING}/*`} element={<Harvesting />} />
                        <Route path={`${PROCESS_ROUTES.WEIGHING}/*`} element={<Weighing />} />
                        <Route path={`${PROCESS_ROUTES.REPORTING}/*`} element={<Reporting />} />
                        <Route path={`${PROCESS_ROUTES.PHOTO}/*`} element={<Photographing />} />
                        <Route
                            path={`${MANAGEMENT_ROUTES.SEED_TYPES}/*`}
                            element={isGlobalAdmin || isTenantAdmin || isOperator ? <SeedTypeRouting /> : <Navigate to="/" replace />}
                        />
                        <Route
                            path={`${MANAGEMENT_ROUTES.CONTAINERS}/*`}
                            element={isGlobalAdmin || isTenantAdmin ? <ContainerRouting /> : <Navigate to="/" replace />}
                        />
                        <Route
                            path={`${MANAGEMENT_ROUTES.TENANT_MANAGEMENT}/*`}
                            element={isGlobalAdmin ? <TenantManagement /> : <Navigate to="/" replace />}
                        />
                        <Route
                            path={`${MANAGEMENT_ROUTES.TEMPLATES}/*`}
                            element={isGlobalAdmin || isTenantAdmin ? <TemplateManagement /> : <Navigate to="/" replace />}
                        />
                        <Route
                            path={`${MANAGEMENT_ROUTES.GLOBAL_USER_MANAGEMENT}/*`}
                            element={isGlobalAdmin ? <GlobalUserManager /> : <Navigate to="/" replace />}
                        />
                        <Route
                            path={`${MANAGEMENT_ROUTES.TENANT_USERS}/*`}
                            element={isGlobalAdmin || isTenantAdmin ? <List /> : <Navigate to="/" replace />}
                        />
                        <Route path={`${MANAGEMENT_ROUTES.CHANGE_PASSWORD}/*`} element={<ChangePassword />} />
                        <Route
                            path={`${MANAGEMENT_ROUTES.PRODUCE_TYPES}/*`}
                            element={isGlobalAdmin || isTenantAdmin || isOperator || isMarketing ? <ProduceTypeRouting /> : <Navigate to="/" replace />}
                        />
                        <Route path={`${ALLOWED_DEEP_LINK_PATHS.PROCESS_START}/*`} element={<ProcessStart />} />
                        <Route path="/" key={location.key} element={<LandingPage contentSlides={landingPageSlides} />} />
                        {isMobile2BEnabled && (
                            <Route path={`${ALLOWED_DEEP_LINK_PATHS.LAUNCH_MOBILE2B}`} element={<LandingPage contentSlides={landingPageSlides} initialSlide={2} />} />
                        )}
                        <Route path='*' element={<LandingPage contentSlides={landingPageSlides} />} />
                    </Routes>
                </SignalRProvider>
            );
        }

        return (
            <Routes>
                {/* Public Routes */}
                <Route path="*" element={<LoginScreen />} />
            </Routes>
        );
    };

    return (
        <HarvestingProceduresProvider>
            <SeedTypeManagementState>
                <ProduceTypeProvider>
                    <Layout>
                        {loading && !authDone ? <Loading fullScreen /> : renderRoutes()}
                    </Layout>
                </ProduceTypeProvider>
            </SeedTypeManagementState>
        </HarvestingProceduresProvider>
    );
};

export default App;
